<template>
  <div v-if="!isLoading" class="p-4 max-w-xl mx-auto">
    <div
      @click="$router.back()"
      class="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 cursor-pointer shadow-md transition"
    >
      <BackSvg />
    </div>
    <div class="flex flex-col justify-center items-center">
      <h2 class="text-lg text-zinc-700 text-center font-semibold">
        Track your Queuing
      </h2>
      <h4 class="text-sm text-zinc-500">#{{ transactionId }}</h4>
    </div>
    <div
      class="relative bg-white border border-gray-200 rounded-2xl shadow-lg my-4 overflow-hidden"
    >
      <!-- Left cutout -->
      <div
        class="absolute top-1/2 -left-3 -translate-y-1/2 w-6 h-6 bg-gray-50 border border-gray-200 rounded-full"
      ></div>

      <!-- Right cutout -->
      <div
        class="absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 bg-gray-50 border border-gray-200 rounded-full"
      ></div>

      <!-- Top Section -->
      <div
        class="flex flex-col gap-3 justify-center items-center py-8 bg-gradient-to-r from-emerald-50 to-white"
      >
        <img
          v-if="itemData.status === 'in-session'"
          src="../../assets/images/consultation.gif"
          alt=""
          class="h-36 w-36"
        />
        <span class="px-14">
          <h1
            :class="`${
              !isNaN(queuingMessage)
                ? 'text-7xl text-orange-500'
                : 'text-2xl text-emerald-500'
            } font-extrabold tracking-tight text-center`"
          >
            {{ queuingMessage }}
          </h1>
        </span>

        <h4
          class="text-zinc-500 text-sm text-center"
          v-if="itemData.status === 'admitted'"
        >
          The doctor will be with you shortly.
        </h4>
        <h4
          class="text-zinc-500 text-sm text-center"
          v-if="itemData.status === 'ringing'"
        >
          Please click the button to join the doctor video consultation
        </h4>
        <h4
          class="text-zinc-500 text-sm"
          v-if="itemData.status === 'in-session'"
        >
          Please wait until the doctor completes your consultation.
        </h4>
      </div>

      <!-- Divider -->
      <div class="border-t border-dashed border-gray-300"></div>

      <!-- Bottom Section -->
      <div
        class="flex gap-2 justify-center flex-col items-center py-4 bg-gray-50"
      >
        <span class="text-sm font-semibold text-gray-600 tracking-wider">
          🎟️ QUEUE {{ !isNaN(queuingMessage) ? "NUMBER" : "STATUS" }}
        </span>
        <span class="text-xs text-zinc-400">
          <h5>{{ dateFormat(itemData.createdat) }}</h5>
        </span>
      </div>
    </div>

    <div class="max-w-md w-full mx-auto" v-if="isReasonCancel">
      <h2 class="py-2 text-sm font-semibold">Reason to cancel</h2>
      <ul class="space-y-2">
        <li v-for="(item, index) in itemReasonCancel" :key="index">
          <label
            class="flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-zinc-50 peer-checked:border-orange-500"
          >
            <!-- Hide the native radio -->
            <input
              v-model="selectedReason"
              type="radio"
              name="cancelReason"
              :value="item.reason"
              @click="alertVisibile = false"
              class="sr-only peer"
            />

            <!-- Custom circle -->
            <span
              class="h-5 w-5 rounded-full border border-gray-300 flex items-center justify-center peer-checked:border-orange-500 peer-checked:bg-orange-500"
            >
              <span
                class="h-2.5 w-2.5 rounded-full bg-white peer-checked:bg-white"
              ></span>
            </span>

            <span class="text-gray-700 text-sm">{{ item.reason }}</span>
          </label>
        </li>
        <div v-if="selectedReason === 'Other'" class="mt-2">
          <textarea
            v-model="otherReason"
            @input="alertVisibile = false"
            rows="3"
            placeholder="Please specify your reason..."
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm resize-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
          ></textarea>
        </div>
      </ul>
    </div>
    <div class="space-y-3 pb-4">
      <div class="w-full" v-if="!isReasonCancel">
        <a
          v-show="joinVideoCall"
          @click="videocallHandler()"
          rel="noopener noreferrer"
          class="block w-full py-8 rounded-lg bg-blue-500 text-white font-semibold tracking-wide text-center hover:bg-blue-600 active:bg-blue-700 transition"
        >
          Join Doctor Video Consultation
        </a>
      </div>
      <div class="w-full" v-if="!isReasonCancel">
        <button
          v-if="itemData.status === 'completed' || itemData.status === 'denied'"
          @click="$router.push('/portal')"
          class="relative w-full py-3 rounded-lg bg-blue-500 text-white font-semibold tracking-wide hover:bg-blue-600 active:bg-blue-700 transition overflow-hidden"
        >
          <span class="relative z-10">Back to Home</span>
          <!-- Progress Overlay -->
        </button>
        <div
          v-else-if="itemData.status === 'in-session'"
          class="flex justify-center items-center gap-2"
        >
          <button
            @click="$router.go(0)"
            class="relative w-full py-3 rounded-lg bg-blue-500 text-white font-semibold tracking-wide hover:bg-blue-600 active:bg-blue-700 transition overflow-hidden"
          >
            <span class="relative z-10">Reload Page</span>
            <!-- Progress Overlay -->
          </button>
          <button
            v-if="false"
            class="relative w-full py-3 rounded-lg bg-blue-500 text-white font-semibold tracking-wide hover:bg-blue-600 active:bg-blue-700 transition overflow-hidden"
          >
            Hello
          </button>
          <button
            v-if="false"
            @click="initializeReasonCancel()"
            class="relative w-full py-3 rounded-lg bg-red-500 text-white font-semibold tracking-wide hover:bg-red-600 active:bg-red-700 transition overflow-hidden"
          >
            End Session
          </button>
        </div>

        <button
          v-else
          @click="isReasonCancel = true"
          class="relative w-full py-3 rounded-lg bg-red-500 text-white font-semibold tracking-wide hover:bg-red-600 active:bg-red-700 transition overflow-hidden"
        >
          <span class="relative z-10">Cancel Consultation </span>
          <!-- Progress Overlay -->
        </button>
      </div>
      <div class="w-full my-3" v-if="isReasonCancel">
        <button
          @click="initializeReasonCancel()"
          class="relative w-full py-3 rounded-lg bg-orange-500 text-white font-semibold tracking-wide hover:bg-orange-600 active:bg-orange-700 transition overflow-hidden"
        >
          <span class="relative z-10">Submit</span>
          <!-- Progress Overlay -->
        </button>
      </div>
      <div class="w-full" v-if="isReasonCancel">
        <button
          @click="handleBack()"
          class="relative w-full py-3 rounded-lg bg-blue-500 text-white font-semibold tracking-wide hover:bg-blue-600 active:bg-blue-700 transition overflow-hidden"
        >
          <span class="relative z-10">Back</span>
          <!-- Progress Overlay -->
        </button>
      </div>
      <div v-if="alertVisibile">
        <span
          class="block w-full p-3 rounded-lg bg-red-50 text-red-600 text-sm font-medium border border-red-200"
        >
          {{ alertMessage }}
        </span>
      </div>
    </div>
    <div
      v-if="itemData.status === 'in-line'"
      class="bg-zinc-100 border border-zinc-200 rounded-lg p-3"
    >
      <h3 class="text-sm text-zinc-700 font-semibold">Takeaway Notes:</h3>
      <div class="text-sm text-zinc-500 mt-2">
        <ul class="list-decimal list-inside space-y-1">
          <li>Ensure your device has enough battery or keep it plugged in</li>
          <li>Use a stable internet connection to avoid interruptions</li>
          <li>Stay in a quiet, comfortable place for better focus</li>
          <li>
            Join a few minutes early so your consultation can start on time
          </li>
        </ul>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="flex items-center justify-center h-48" v-if="isLoading">
      <svg
        class="w-10 h-10 animate-spin text-orange-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        ></path>
      </svg>
    </div>
  </div>
</template>

<script>
import { computed, inject, onMounted, ref } from "vue";
import BackSvg from "@/components/svgs/BackSvg.vue";
import { useRoute } from "vue-router";
import socket from "@/utils/socket";

export default {
  components: {
    BackSvg,
  },
  setup() {
    const axios = inject("$axios");
    const router = inject("$router");
    const moment = inject("$moment");
    const route = useRoute();
    const transactionId = ref();
    const itemData = ref([]);
    const isLoading = ref(false);
    const selectedReason = ref(null);
    const otherReason = ref(null);
    const isReasonCancel = ref(false);
    const alertMessage = ref();
    const alertVisibile = ref();
    const itemReasonCancel = ref([
      {
        reason: "Too long waiting",
      },
      {
        reason: "Technical issue",
      },
      {
        reason: "Changed my mind",
      },
      {
        reason: "Other",
      },
    ]);

    onMounted(() => {
      transactionId.value = route.params.id;
      currentQueuingHandler();
    });

    const queuingCancelHandler = async () => {
      try {
        await axios.put(`/api/patient/queuing/${transactionId.value}`, {
          status: "cancel",
          reasoncancel: !otherReason.value
            ? selectedReason.value
            : otherReason.value,
        });
        router.push(`/portal`);
      } catch (error) {
        console.log(error);
      }
    };

    const currentQueuingHandler = async () => {
      try {
        isLoading.value = true;
        const { data } = await axios.get(
          `/api/patient/transaction/queuing/${transactionId.value}`
        );
        itemData.value = data.data;
        if (data.data === undefined) {
          router.push({ name: "NotFound" });
        }
        socket.emit("registerPatient", { transactionid: transactionId.value });

        // Avoid stacking multiple listeners
        socket.off("updateQueuing");

        socket.on("updateQueuing", (data) => {
          if (data.transactionid !== transactionId.value) return;
          itemData.value = data;
        });

        setTimeout(() => {
          isLoading.value = false;
        }, 800);
      } catch (error) {
        console.log(error);
      }
    };

    const queuingMessage = computed(() => {
      let status = itemData.value.status;
      let result;

      if (status === "in-line") {
        result = itemData.value.spot_number;
      } else if (status === "admitted") {
        result = "Your turn";
      } else if (status === "ringing") {
        result = "The doctor has joined the video room.";
      } else if (status === "in-session") {
        result = "You are currently in session";
      } else if (status === "denied") {
        result = "You are currently rejected";
      } else if (status === "completed") {
        result = "The consultation was completed.";
      }
      return result;
    });

    const joinVideoCall = computed(() => {
      return itemData.value.status === "ringing" ? true : false;
    });

    const videocallHandler = async () => {
      try {
        await axios.put(`/api/patient/queuing/action/${transactionId.value}`);
        if (itemData.value.patientLink) {
          window.open(itemData.value.patientLink, "_blank");
        }
      } catch (error) {
        console.log(error);
      }
    };

    const dateFormat = (val) => {
      return moment(val).format("LLL");
    };
    const initializeReasonCancel = () => {
      if (selectedReason.value) {
        if (selectedReason.value === "Other" && !otherReason.value) {
          alertVisibile.value = true;
          alertMessage.value = "Please specify your reason for cancellation.";
        } else {
          alertVisibile.value = false;
          queuingCancelHandler();
        }
      } else {
        alertVisibile.value = true;
        alertMessage.value = "Please select a cancellation reason";
      }
    };
    const handleBack = () => {
      selectedReason.value = "";
      isReasonCancel.value = false;
      alertVisibile.value = false;
    };
    return {
      transactionId,
      queuingCancelHandler,
      currentQueuingHandler,
      itemData,
      queuingMessage,
      joinVideoCall,
      videocallHandler,
      dateFormat,
      isLoading,
      itemReasonCancel,
      isReasonCancel,
      initializeReasonCancel,
      selectedReason,
      otherReason,
      alertMessage,
      alertVisibile,
      handleBack,
    };
  },
};
</script>
